// R58-L-13 — Viewer bbox toggle store.
//
// Two independent booleans: show AI-finding overlays, show Defect
// overlays. State is persisted to `?aiToggle` + `?defectToggle` URL
// search params so the toggle state is shareable + survives reloads
// (story AC6).
//
// The store itself only holds the booleans; the URL hydration + sync
// runs as a single effect mounted at the Viewer's host (`useBBoxTogglesUrlSync`).
// The split keeps `useBBoxToggles` testable without router context.
import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { create } from "zustand";

export interface BBoxTogglesState {
  aiVisible: boolean;
  defectVisible: boolean;
  setAiVisible: (next: boolean) => void;
  setDefectVisible: (next: boolean) => void;
  toggleAi: () => void;
  toggleDefect: () => void;
  /** Replace both flags at once — used by the URL-sync effect on initial mount. */
  hydrate: (state: { aiVisible: boolean; defectVisible: boolean }) => void;
}

export const useBBoxToggles = create<BBoxTogglesState>((set) => ({
  aiVisible: true,
  defectVisible: true,
  setAiVisible: (next) => {
    set({ aiVisible: next });
  },
  setDefectVisible: (next) => {
    set({ defectVisible: next });
  },
  toggleAi: () => {
    set((s) => ({ aiVisible: !s.aiVisible }));
  },
  toggleDefect: () => {
    set((s) => ({ defectVisible: !s.defectVisible }));
  },
  hydrate: ({ aiVisible, defectVisible }) => {
    set({ aiVisible, defectVisible });
  },
}));

function parseFlag(raw: string | null, fallback: boolean): boolean {
  if (raw === null) return fallback;
  return raw.toLowerCase() === "on";
}

/**
 * Mount once at the Viewer host. On first paint, reads `aiToggle` +
 * `defectToggle` from the URL and hydrates the store. On every store
 * change, writes the values back to the URL (replace-state so the
 * back-button isn't polluted with every flip).
 */
export function useBBoxTogglesUrlSync(): void {
  const [searchParams, setSearchParams] = useSearchParams();
  const aiVisible = useBBoxToggles((s) => s.aiVisible);
  const defectVisible = useBBoxToggles((s) => s.defectVisible);
  const hydrate = useBBoxToggles((s) => s.hydrate);

  // Hydrate from URL on first mount only (then store -> URL is the
  // source of truth until next reload). `hydratedRef` blocks the
  // re-hydration that would otherwise fire when the URL-sync effect
  // below writes the canonical values back to the address bar.
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;
    const aiFromUrl = parseFlag(searchParams.get("aiToggle"), true);
    const defectFromUrl = parseFlag(searchParams.get("defectToggle"), true);
    hydrate({ aiVisible: aiFromUrl, defectVisible: defectFromUrl });
  }, [searchParams, hydrate]);

  // Mirror store -> URL on every change (replace mode keeps history clean).
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    next.set("aiToggle", aiVisible ? "on" : "off");
    next.set("defectToggle", defectVisible ? "on" : "off");
    // Only update if changed to avoid render loops.
    if (next.toString() !== searchParams.toString()) {
      setSearchParams(next, { replace: true });
    }
  }, [aiVisible, defectVisible, searchParams, setSearchParams]);
}
